import {
  FormSchemasTableFilterArea,
  FormSchemasTableFilterAreaContent,
} from "#src/batteries-included-components/FilterAreas/FormsFilterAreas";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import { useTableSortingAndPagination } from "#src/components/Redux/reducers/tableStateReducer";
import { useListFormSchemas } from "#src/components/hooks/adapters/useFormSchema";
import { useStickyState } from "#src/hooks/useStickyState";
import { linkToFormCategoryDetails } from "#src/routes/forms/categories/[categoryId]";
import { linkToFormTemplateDetail } from "#src/routes/forms/categories/[categoryId]/templates/[formTemplateId]";
import { useDataTableUserInterfaceConfiguration } from "#src/utils/table";
import { FormSchemaStatusToPillVariantMapping } from "#src/view-models/forms";
import {
  DataTable,
  DataTablePanel,
  HeaderType,
  Pill,
  StorageKeys,
} from "@validereinc/common-components";
import {
  FormSchemaAdapter,
  type FormSchemaType,
  getFilterObject,
  SortDirection,
} from "@validereinc/domain";
import { toTitleCaseString } from "@validereinc/utilities";
import React from "react";

export const FormSchemasTablePanel = ({
  tableConfigStorageKey,
  filterConfigStorageKey,
  tableStateStorageKey,
}: Pick<
  StorageKeys,
  "tableConfigStorageKey" | "filterConfigStorageKey" | "tableStateStorageKey"
>) => {
  const {
    saveDefaultConfigurationMutation,
    isSaveConfigurationAvailable,
    resetConfigurationMutation,
    deleteDefaultConfigurationMutation,
    isDeleteConfigurationAvailable,
    isLoading: isLoadingTableUserInterfaceConfiguration,
    matchingDefaultConfiguration,
  } = useDataTableUserInterfaceConfiguration(tableConfigStorageKey);
  const [filters] = useStickyState<{
    name?: string;
  }>({}, filterConfigStorageKey);
  const [tableState, updateTableState] = useTableSortingAndPagination(
    {
      sortBy: "name",
      sortDirection: SortDirection.ASCENDING,
    },
    filters,
    tableStateStorageKey
  );

  const queryPayload: Parameters<typeof FormSchemaAdapter.getList>[0] = {
    filters: {
      ...getFilterObject(filters),
      status: "active",
      // @ts-expect-error TODO: Typing on filter
      isAlreadyFormatted: true,
    },
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
  };

  const formSchemasQuery = useListFormSchemas(queryPayload);

  const headers: Array<HeaderType<FormSchemaType>> = [
    {
      label: "Name",
      key: "name",
      isSortable: true,
      renderComponent: ({ item }) => {
        return (
          <RoutingLink
            to={linkToFormTemplateDetail(item.form_category_id, item.id)}
          >
            {item.name}
          </RoutingLink>
        );
      },
    },
    {
      label: "Category",
      key: "form_category_id",
      isSortable: true,
      renderComponent: ({ item }) => {
        if (!item.form_category_id) {
          return "-";
        }

        return (
          <RoutingLink to={linkToFormCategoryDetails(item.form_category_id)}>
            {item.form_category?.name ?? "n/a"}
          </RoutingLink>
        );
      },
    },
    {
      label: "Status",
      key: "status",
      isSortable: true,
      renderComponent: ({ item }) => (
        <Pill
          variant={
            FormSchemaStatusToPillVariantMapping[item.status] ?? "default"
          }
        >
          {toTitleCaseString(item.status, {
            intent: "ui",
            exceptions: ["oil-and-gas"],
          })}
        </Pill>
      ),
    },
    {
      label: "Description",
      key: "description",
      isSortable: true,
      renderComponent: ({ item }) => (
        <DataTable.DataRow.TextCell value={item.description} />
      ),
    },
    {
      label: "Last Updated",
      key: "updated_at",
      isSortable: true,
      renderComponent: ({ item }) => (
        <DataTable.DataRow.DateCell
          value={item.updated_at}
          withTime
        />
      ),
    },
  ];

  return (
    <DataTablePanel
      storageKey={tableConfigStorageKey}
      panelProps={{
        title: "Templates",
      }}
      dataTableProps={{
        intent: "simplicity-first",
        headers,
        items: formSchemasQuery.data?.data ?? [],
        getItemId: (item) => item.id,
        isLoading: formSchemasQuery.isLoading,
        sorting: {
          sortBy: tableState.sortBy,
          sortDirection: tableState.sortDirection,
        },
        pagination: {
          page: tableState.page,
          pageSize: tableState.pageSize,
          total: formSchemasQuery.data?.total_entries,
          pageSizeText: "rows per page",
        },
        onSortChange: updateTableState,
        onPaginationChange: updateTableState,
      }}
      filterComponent={
        <FormSchemasTableFilterArea
          filterConfigStorageKey={filterConfigStorageKey}
          filterDrawerContentSlot={<FormSchemasTableFilterAreaContent />}
        />
      }
      columnConfigurationProps={{
        onSaveColumnConfigurationAsDefault: (colsToSave) =>
          saveDefaultConfigurationMutation
            .mutateAsync({
              name: "Default for Form Templates list table",
              configuration: {
                columns: colsToSave,
              },
            })
            .then(() => true)
            .catch(() => false),
        isLoadingSaveDefaultColumnConfiguration:
          saveDefaultConfigurationMutation.isLoading,
        isSaveDefaultColumnConfigurationAvailable: isSaveConfigurationAvailable,
        onResetColumnConfiguration: () =>
          resetConfigurationMutation.mutateAsync(),
        onDeleteDefaultColumnConfiguration: () =>
          deleteDefaultConfigurationMutation
            .mutateAsync()
            .then(() => true)
            .catch(() => false),
        isLoadingDeleteDefaultColumnConfiguration:
          deleteDefaultConfigurationMutation.isLoading,
        isDeleteDefaultColumnConfigurationAvailable:
          isDeleteConfigurationAvailable,
        isLoadingDefaultColumnConfiguration:
          isLoadingTableUserInterfaceConfiguration,
        isDefaultColumnConfigurationAvailable: !!matchingDefaultConfiguration,
        defaultColumnConfiguration:
          matchingDefaultConfiguration?.configuration?.columns,
      }}
    />
  );
};
